/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


// let lastScrollTop = 100;
//   const element = document.querySelector('store-header');

//   window.addEventListener('scroll', function () {
//     const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

//     if (scrollTop > lastScrollTop) {
//     if (scrollDownStart === null) {
//       scrollDownStart = scrollTop;
//     }

//     if (scrollTop - scrollDownStart >= 100) {
//       element.classList.add('scrolled-down');
//     }
//   } else if (scrollTop < lastScrollTop) {
//     element.classList.remove('scrolled-down');
//     scrollDownStart = null;
//   }
//   console.log(scrollTop, 'scrollTop')

//     lastScrollTop = scrollTop <= 100 ? 100 : scrollTop;
//   }, false);

let lastScrollTop = 100;
const element = document.querySelector('store-header');
let scrollDownStart = null;

window.addEventListener('scroll', function () {
  if (window.innerWidth > 767) {
    // Do nothing if viewport is wider than 767px
    return;
  }

  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  if (scrollTop > lastScrollTop) {
    if (scrollDownStart === null) {
      scrollDownStart = scrollTop;
    }

    if (scrollTop - scrollDownStart >= 100) {
      element.classList.add('scrolled-down');
    }
  } else if (scrollTop < lastScrollTop) {
    element.classList.remove('scrolled-down');
    scrollDownStart = null;
  }

  // console.log(scrollTop, 'scrollTop');

  lastScrollTop = scrollTop <= 100 ? 100 : scrollTop;
}, false);

// Seacrh toggle view
document.addEventListener('change', function(event) {
  if (event.target.name === 'search-products-layout') {
    const searchList = document.querySelector('#header-search-products .predictive-search__list');
    if (searchList) {
      searchList.classList.remove('list-view', 'grid-view');
      searchList.classList.add(event.target.value === 'grid' ? 'grid-view' : 'list-view');
    }
  }
});
// Show More Variants
const showMoreBtn = document.querySelector('.show_more');

if (showMoreBtn) {
  showMoreBtn.addEventListener('click', function() {
    const hiddenLabels = document.querySelectorAll('label.mobile-hidden-only');
    hiddenLabels.forEach(label => {
      label.classList.remove('mobile-hidden-only');
    });
    this.style.display = 'none';
  });
}

// document.querySelector('.show_more').addEventListener('click', function() {
//   const hiddenLabels = document.querySelectorAll('label.mobile-hidden-only');
//   hiddenLabels.forEach(label => {
//     label.classList.remove('mobile-hidden-only');
//   });
//   this.style.display = 'none';
// });

// Make header balck when search is open
const predictiveSearch = document.querySelector('predictive-search');
const header = document.querySelector('.header');
if (predictiveSearch) {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        if (predictiveSearch.hasAttribute('open')) {
          header.classList.add('header-scrolled');
        } else {
          header.classList.remove('header-scrolled');
        }
      }
    });
  });

  observer.observe(predictiveSearch, { attributes: true });
}

jQuery('.close-menu').on('click', function(){
  jQuery(this).parents('.main-menu__disclosure').removeClass('is-open');
  jQuery('body').removeClass('overflow-hidden');
  setTimeout(function(){
    jQuery('.main-menu .main-menu__disclosure').removeAttr('open');
  }, 300)
});

jQuery('.header__grid .menu-overlay').on('click', function(){
  jQuery('.main-menu--left-mob .main-menu__disclosure').removeClass('is-open');
  jQuery('body').removeClass('overflow-hidden');
  setTimeout(function(){
    jQuery('.main-menu .main-menu__disclosure').removeAttr('open');
  }, 300)
});

jQuery('.menu-search--plus-menu-close .search-input').on('click', function(){
  jQuery('.main-menu--left-mob .main-menu__disclosure').removeClass('is-open');
  jQuery('body').removeClass('overflow-hidden');
  setTimeout(function(){
    jQuery('.main-menu .main-menu__disclosure').removeAttr('open');
    jQuery('store-header.header').removeClass('search-is-collapsed').addClass('search-is-visible');
    jQuery('predictive-search input.search__input').focus();
  }, 300)
})

$(document).ready(function() {
  $(document).on('click', 'facet-filters button.drawer__close-btn', function() {
    $('store-header').addClass('scrolled-down');
  });
});

// Sticky Filters on mobile
function initToolbarScroll() {
  const toolbar = document.querySelector('products-toolbar');
  const pagination = document.querySelector('custom-pagination');

  if (toolbar && pagination) {
    const toolbarOriginalTop = toolbar.getBoundingClientRect().top + window.pageYOffset;

    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const toolbarHeight = toolbar.offsetHeight;

      if (scrollTop >= toolbarOriginalTop) {
        if (!toolbar.classList.contains('fixed-header')) {
          toolbar.classList.add('fixed-header');
          pagination.style.paddingTop = toolbarHeight + 'px';
        }
      } else {
        if (toolbar.classList.contains('fixed-header')) {
          toolbar.classList.remove('fixed-header');
          pagination.style.paddingTop = '';
        }
      }
    });

    return true;
  }
  return false;
}

function handleScrollDirection() {
  let lastScrollTop = 0;
  const toolbar = document.querySelector('products-toolbar');
  const storeHeader = document.querySelector('store-header');
  const header = document.querySelector('header'); // Adjust selector as needed

  if (!(toolbar && storeHeader && header)) return;

  window.addEventListener('scroll', () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const storeHeaderHeight = storeHeader.offsetHeight - 2;
    const scrollDownDistance = scrollTop - lastScrollTop;

    if (toolbar.classList.contains('fixed-header')) {
      // Check if scrolled-down class is NOT on header
      if (!header.classList.contains('scrolled-down')) {
        if (scrollTop < lastScrollTop) {
          // Scrolled up and no scrolled-down class on header
          toolbar.style.top = storeHeaderHeight + 'px';
        } else if (scrollDownDistance > 1) {
          
          toolbar.style.top = '';
        }
      } else {
        // If scrolled-down class is present, remove top
        toolbar.style.top = '';
      }
    } else {
      toolbar.style.top = '';
    }

    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  });
}

function waitForElements() {
  if (!initToolbarScroll()) {
    setTimeout(waitForElements, 100);
  }
  handleScrollDirection();
}

document.addEventListener('DOMContentLoaded', waitForElements);
// EO Sticky filters

// Progress bar
// function updateTieredProgressBar(cartTotal, cart) {
//   var thresholds = {
//     'AUD': { first: 250, second: 500 },
//     'USD': { first: 200, second: 400 },
//     'CAD': { first: 250, second: 500 },
//     'GBP': { first: 150, second: 300 },
//     'EUR': { first: 200, second: 400 },
//     'CHF': { first: 200, second: 400 }
//   };
  
//   console.log('progrss bar updated');
  

//   var progressBar = document.querySelector('.tiered-progress-bar');
//   var fill = document.querySelector('.progress-bar-fill');
//   var car = document.querySelector('.progress-bar-car');
//   var miniMsg = document.querySelector('.progress-bar-mini-message');
//   var msg = document.querySelector('.progress-bar-message');
//   var symbol = progressBar.dataset.currencySymbol || '$';
//   var stepOneElement = document.querySelector('.progress-bar-step[data-step="5off"]');
//   var stepTwoElement = document.querySelector('.progress-bar-step[data-step="10off"]');
//   var changeZindex = document.querySelector('.progress-bar-step[data-step="5off"]');
//   var currency = Shopify.currency.active || 'AUD';

//   if (!thresholds.hasOwnProperty(currency)) {
//     progressBar.style.display = 'none';
//     return;
//   } else {
//     progressBar.style.display = '';
//   }

//   var config = thresholds[currency];
//   var firstThreshold = config.first;
//   var secondThreshold = config.second;

//   var percent = Math.min((cartTotal / secondThreshold) * 100, 100);
  
//   if(percent < 12){
//     fill.style.width = '12%';
//     car.style.width  = '12%';
//   } else {
//     fill.style.width = percent + '%';
//     car.style.width  = percent + '%';

//   }
  
//   if (cartTotal < firstThreshold) {
//     if(symbol == 'CHF') {
//       msg.innerHTML = `Add ${symbol} ${(firstThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${symbol} 50 Gift</span>`;  
//     } else {
//       msg.innerHTML = `Add ${symbol}${(firstThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${symbol}50 Gift</span>`;
//     }
    
//     miniMsg.innerHTML = `Free Shipping Unlocked`;
//     if (stepTwoElement.classList.contains('clear')) {
//       stepTwoElement.classList.remove('clear');
//     }
//     if (stepOneElement.classList.contains('clear')) {
//       stepOneElement.classList.remove('clear');
//     }
//   } else if (cartTotal < secondThreshold) {
//     if(symbol == 'CHF') {
//       msg.innerHTML = `Add ${symbol} ${(secondThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${symbol} 150 Gift</span>`;
//       miniMsg.innerHTML = `Free Shipping + ${symbol} 50 Free Gift Unlocked`;
//     } else {
//       msg.innerHTML = `Add ${symbol}${(secondThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${symbol}150 Gift</span>`;
//       miniMsg.innerHTML = `Free Shipping + ${symbol}50 Free Gift Unlocked`;
//     }
//     stepOneElement.classList.add('clear');
//     if (stepTwoElement.classList.contains('clear')) {
//       stepTwoElement.classList.remove('clear');
//     }
//   } else {
//     msg.innerHTML = `Congrats! <span class="highlight">You've unlocked ${symbol}150 free gift!</span>`;
//     miniMsg.innerHTML = `Free Shipping + ${symbol}150 Free Gift Unlocked`;
//     //miniMsg.innerHTML = `Free Shipping + 10% Off Unlocked`;
//     stepOneElement.classList.add('clear');
//     stepTwoElement.classList.add('clear');
//   }

// }

// Function to check presence of special variant in cart
function hasSpecialVariant(cart, variantId) {
  return cart.items.some(item => item.variant_id === variantId);
}

function hasSecondVariant(cart, variantId) {
  return cart.items.some(item => 
    item.variant_id === variantId && item.final_price !== 0
  );
}

// Function to add special variant to cart
// async function addSpecialVariant(variantId) {
//   await fetch('/cart/add.js', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
//   });
// }

// // Function to remove special variant from cart
// async function removeSpecialVariant(cart, variantId) {
//   const itemToRemove = cart.items.find(item => item.variant_id === variantId);
//   if (itemToRemove) {
//     await fetch('/cart/change.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ id: itemToRemove.key, quantity: 0 }),
//     });
//   }
// }
async function updateCartVariantsToMatch(cart, variantsToAdd, variantsToRemove) {

  if (variantsToAdd.length > 0) {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: variantsToAdd.map(id => ({ id, quantity: 1 })) }),
    });
  }

  const updates = {};
  variantsToRemove.forEach(variantId => {
    const cartItem = cart.items.find(item => item.variant_id === variantId);
    if (cartItem) {
      updates[cartItem.key] = 0;
    }
  });
  
  if (Object.keys(updates).length) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    }).then(response => {
      if (!response.ok) {
        // Debug here
        console.error('Bad request error on cart change', response);
      }
      return response.json();
    });
  }

  setTimeout(function() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  }, 100);
  
  
}


// Main async function to handle add/remove logic and update progress bar
async function mainUpdateTieredProgressBar(cartTotal, cart) {
  const variantsToAdd = [];
  const variantsToRemove = [];

  const paidProduct = hasSecondVariant(cart, window.theme.freeProducts.productTwoId);
  var currency = Shopify.currency.active || 'AUD';
  // console.log('main currency', currency);
  var thresholds = {
    'AUD': { first: 1, second: 500 },
    'USD': { first: 1, second: 400 },
    'CAD': { first: 1, second: 500 },
    'GBP': { first: 1, second: 300 },
    'EUR': { first: 1, second: 400 },
    'CHF': { first: 1, second: 400 }
  };
  var config = thresholds[currency];
  var firstThreshold = config.first;
  var secondThreshold = config.second;
  const firstVariantId = window.theme.freeProducts.productOneId;
  // const secondVariantId = window.theme.freeProducts.productTwoId;
  var msg = document.querySelector('.progress-bar-message');

  // const firstInCart = hasSpecialVariant(cart, firstVariantId);
  // const secondInCart = hasSpecialVariant(cart, secondVariantId);
  if (cartTotal >= secondThreshold) {
    // console.log('threshold cleared');
    if (!hasSpecialVariant(cart, firstVariantId)) variantsToAdd.push(firstVariantId);
    // if (!hasSpecialVariant(cart, secondVariantId)) variantsToAdd.push(secondVariantId);
    // if (hasSecondVariant(cart, secondVariantId)) variantsToRemove.push(secondVariantId);
  // } else if (cartTotal >= firstThreshold) {
    // if (!hasSpecialVariant(cart, firstVariantId)) variantsToAdd.push(firstVariantId);
    // if (hasSpecialVariant(cart, secondVariantId)) variantsToRemove.push(secondVariantId);
    // if (hasSecondVariant(cart, firstVariantId)) variantsToRemove.push(firstVariantId);
  } else {
    // console.log('threshold not cleared');
    if (hasSpecialVariant(cart, firstVariantId)) variantsToRemove.push(firstVariantId);
    // if (hasSpecialVariant(cart, secondVariantId)) variantsToRemove.push(secondVariantId);
  }
  // console.log('variantsToAdd', variantsToAdd);
  // console.log('variantsToRemove', variantsToRemove);
  if( variantsToAdd.length > 0 || variantsToRemove.length > 0 ) {
    if(variantsToAdd.length > 0) {
      msg.innerHTML = `ADDING FREE GIFTS...`;
    } else if (variantsToRemove.length > 0) {
      msg.innerHTML = `REMOVING FREE GIFTS...`;
    }
    await updateCartVariantsToMatch(cart, variantsToAdd, variantsToRemove);
  } else {
    updateTieredProgressBar(cartTotal, cart);
  }
  
}

function updateTieredProgressBar(cartTotal, cart) {
  const firstVariantPrice = window.theme.freeProducts.productOnePrice;
  const firstVariantPWS = window.theme.freeProducts.productOnePriceWithSymbol;
  const secondVariantPrice = window.theme.freeProducts.productTwoPrice;
  const secondVariantPWS = window.theme.freeProducts.productTwoPriceWithSymbol;
  // const variantTotalPrice = window.theme.freeProducts.productTotalPrice;
  const variantTotalPrice = window.theme.freeProducts.productOnePriceWithSymbol;
  
  var progressBar = document.querySelector('.tiered-progress-bar');
  if (!progressBar) return;
  
  var currency = Shopify.currency.active || 'AUD';
  // console.log('currency', currency);
  var thresholds = {
    // 'AUD': { first: 200, second: 400 },
    // 'USD': { first: 150, second: 300 },
    // 'CAD': { first: 200, second: 400 },
    // 'GBP': { first: 100, second: 250 },
    // 'EUR': { first: 150, second: 300 },
    // 'CHF': { first: 150, second: 300 }
    'AUD': { first: 1, second: 500 },
    'USD': { first: 1, second: 400 },
    'CAD': { first: 1, second: 500 },
    'GBP': { first: 1, second: 300 },
    'EUR': { first: 1, second: 400 },
    'CHF': { first: 1, second: 400 }
  };

  if (!thresholds.hasOwnProperty(currency)) {
    progressBar.style.display = 'none';
    return;
  } else {
    progressBar.style.display = '';
  }
  
  var config = thresholds[currency];
  var firstThreshold = config.first;
  var secondThreshold = config.second;

  var fill = document.querySelector('.progress-bar-fill');
  var car = document.querySelector('.progress-bar-car');
  var miniMsg = document.querySelector('.progress-bar-mini-message');
  var msg = document.querySelector('.progress-bar-message');
  var symbol = progressBar.dataset.currencySymbol || '$';
  var stepOneElement = document.querySelector('.progress-bar-step[data-step="5off"]');
  // var stepTwoElement = document.querySelector('.progress-bar-step[data-step="10off"]');
  var percent = Math.min((cartTotal / secondThreshold) * 100, 100);

  if (percent < 15) {
    fill.style.width = '15%';
    car.style.width = '15%';
  } else {
    fill.style.width = percent + '%';
    car.style.width = percent + '%';
  }

  if (cartTotal < firstThreshold) {
    // if (symbol === 'CHF') {
    //   msg.innerHTML = `Add ${symbol} ${(firstThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${firstVariantPWS} Gift</span>`;
    // } else {
    //   msg.innerHTML = `Add ${symbol}${(firstThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${firstVariantPWS} Gift</span>`;
    // }
    miniMsg.innerHTML = `Free Shipping Unlocked`;

    // stepTwoElement.classList.remove('clear');
    stepOneElement.classList.remove('clear');
  } else if (cartTotal < secondThreshold) {
    if (symbol === 'CHF') {
      msg.innerHTML = `Add ${symbol} ${(secondThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${firstVariantPWS} Gift</span>`;
      // miniMsg.innerHTML = `Free Shipping + ${firstVariantPWS} Free Gift Unlocked`;
      miniMsg.innerHTML = `Free Shipping Unlocked`;
    } else {
      msg.innerHTML = `Add ${symbol}${(secondThreshold - cartTotal).toFixed(0)} to unlock <span class="highlight">A Free ${firstVariantPWS} Gift</span>`;
      // miniMsg.innerHTML = `Free Shipping + ${firstVariantPWS} Free Gift Unlocked`;
      miniMsg.innerHTML = `Free Shipping Unlocked`;
    }

    stepOneElement.classList.remove('clear');
    // stepTwoElement.classList.remove('clear');
  } else {
    msg.innerHTML = `You've unlocked <span class="highlight">${variantTotalPrice} in free gifts!</span>`;
    miniMsg.innerHTML = `🎁 FREE GIFT OFFER`;

    stepOneElement.classList.add('clear');
    // stepTwoElement.classList.add('clear');
  }
}


document.addEventListener('DOMContentLoaded', function() {

  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      const tieredBar = document.querySelector('.tiered-progress-bar');
      const normalBar = document.querySelector('.normal-progress-bar');
      const brands = ['AUDI', 'MERCEDES', 'BMW'];
      const inCartBrands = [...new Set(cart.items.map(item => brands.find(b => item.product_title.toUpperCase().includes(b))).filter(Boolean))].join(', ');
      var cartTotal = cart.total_price / 100;
      if (inCartBrands.includes('BMW')) {
        // console.log('cart on load BMW', cart);
        if (tieredBar?.classList.contains('hidden')) {
          tieredBar?.classList.remove('hidden');
        }
        if (!normalBar?.classList.contains('hidden')) {
          normalBar?.classList.add('hidden');
        }
        mainUpdateTieredProgressBar(cartTotal, cart);
      } else {
        // console.log('cart on load NON-BMW', cart);
        if (!tieredBar?.classList.contains('hidden')) {
          tieredBar?.classList.add('hidden');
        }
        if (normalBar?.classList.contains('hidden')) {
          normalBar?.classList.remove('hidden');
        }
      }
      // mainUpdateTieredProgressBar(cartTotal, cart);
    });
});
document.addEventListener('dispatch:cart-drawer:refresh', function(event) {
  var intervalId = setInterval(() => {
    var fill = document.querySelector('.progress-bar-fill');
    if (fill && fill.style.width === '12%') {
      clearInterval(intervalId); // Stop checking once condition met

      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          var cartTotal = cart.total_price / 100;
          // console.log('refrsh cart', cart);
          updateTieredProgressBar(cartTotal, cart);
        });
    }
  }, 1000);

  
});
// EO of progress bar
// chat popup function
$('.secondary-nav__item.secondary-nav__cta.secondary-nav__cta--button').click(function(e){
  e.preventDefault();
  $('.woot-widget-bubble.woot-elements--right').trigger('click');
})

// custom li in custom selector on product page
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('custom-select').forEach(function(select) {
    var listbox = select.querySelector('.custom-select__listbox');
    var guide   = select.querySelector('.custom-select__fitment-guide');
    if (!listbox || !guide) return;

    guide.addEventListener('mousedown', function(e) {
      e.preventDefault();
    });

    var link = guide.querySelector('a');
    if (link) {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation(); 
        var popupType = this.getAttribute('data-popup');
        var selector = '.option-selector modal-opener[data-modal="' + popupType + '"] button.link.block';
        $(selector).trigger('click');

        if (typeof select.hideListbox === 'function') {
          select.hideListbox();
        }
      });
    }
  });
});

